// src/features/legacy-reports/LegacyReportsApi.ts
//
// Static API class over the shared webApi singleton for the UNCHANGED legacy
// /v1/reports1 surface (POST only). The four report routes take cId as a
// QUERY parameter and bodies carry only the fields the Scala DTOs read; the
// two query/CSV routes take cId inside the JSON body. Timestamps are UTC
// epoch seconds both directions.

import { webApi, type BlobResponse } from '@/shared/api/WebApi';
import type { LegacyReportSubtype } from '@/lib/constants/legacyReports';
import type {
  LegacyAccountReport,
  LegacyMediasDetailReport,
  LegacyMediasReport,
} from './types';

export class LegacyReportsApi {
  static async getAccountReport(
    cId: number,
    start: number,
    end: number
  ): Promise<LegacyAccountReport> {
    return webApi.post<LegacyAccountReport>('reports1/account', { cId }, { start, end });
  }

  /** Children of the parent account, aggregated. */
  static async getSubaccountsReport(
    cId: number,
    start: number,
    end: number
  ): Promise<LegacyAccountReport> {
    return webApi.post<LegacyAccountReport>('reports1/subaccounts', { cId }, { start, end });
  }

  static async getMediasReport(
    cId: number,
    start: number,
    end: number,
    mediaIds?: number[]
  ): Promise<LegacyMediasReport> {
    return webApi.post<LegacyMediasReport>(
      'reports1/medias',
      { cId },
      { start, end, mediaIds: mediaIds ?? null }
    );
  }

  static async getMediasDetailReport(
    cId: number,
    start: number,
    end: number,
    mediaIds?: number[]
  ): Promise<LegacyMediasDetailReport> {
    return webApi.post<LegacyMediasDetailReport>(
      'reports1/mediasdetail',
      { cId },
      { start, end, mediaIds: mediaIds ?? null }
    );
  }

  /** Direct synchronous CSV (no job/poll); body cId per the user-query DTO. */
  static async downloadMediasCsv(
    cId: number,
    start: number,
    end: number,
    mediaIds?: number[]
  ): Promise<BlobResponse> {
    return webApi.postBlob('reports1/query/medias/totals', undefined, {
      cId,
      start,
      end,
      mediaIds: mediaIds ?? null,
      format: 'csv',
    });
  }

  static async downloadMediasDetailCsv(
    cId: number,
    start: number,
    end: number,
    mediaIds: number[],
    reportSubtypes: LegacyReportSubtype[]
  ): Promise<BlobResponse> {
    return webApi.postBlob('reports1/query/mediasdetail/totals', undefined, {
      cId,
      start,
      end,
      mediaIds,
      format: 'csv',
      reportSubtypes,
    });
  }
}
